import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppSelect,
  Feedback,
  Logo,
  Notification,
  Profile,
} from "../appearance/components";
import { WE_BANNER_ID } from "../appearance/constant";
import FullScreen from "../full-screen";
import useRequest from "../utils/use-request";
import apis from "../utils/apis";
import type { AppsRes, HeaderOpDoms } from "../appearance/dto";
import type { AppItem } from "../appearance/components/appSelect/dto";
import type { NavigationProps } from "./dto";
import classNames from "classnames";
import { useTheme } from "@gw/css";
import { GwAppearanceTheme } from "../appearance/theme";

const stopPropagation = (e: { stopPropagation: () => void }) => {
  e.stopPropagation();
};
/**
 * 抽离整个head部分
 */
const Navigation: React.FC<NavigationProps> = ({
  collapsed = true,
  app,
  themeCode,
  headerOptionRender,
  showFeedBack = true,
  showFullScreen = false,
  isI18N,
  renderModule,
  questionnaires,
  onAppChange,
  onUserInfoChange,
  onUserLogout,
  onAppsChange,
}) => {
  const { themeConfig } = useTheme<GwAppearanceTheme>();

  const headerContentRef = useRef<HTMLDivElement>();
  const [appList, setAppList] = useState<AppItem[]>([]);
  const [, fetchApps, ref] = useRequest<never, AppsRes>(apis.user.applications);
  useEffect(() => {
    if (app) {
      fetchApps()
        .then((data) => {
          setAppList(data.dataList);
        })
        .catch((e) => {
          console.log(`[wbuc]: Fetch applications error`, e);
        });
    }
    return () => ref.abort();
  }, [app]);

  useEffect(() => {
    onAppsChange?.(appList.slice(0));
  }, [appList]);

  const operations = useMemo(() => {
    const doms = {} as HeaderOpDoms;
    if (questionnaires) {
      doms.Questionnaires = <Notification {...questionnaires} />;
    }
    if (headerOptionRender) {
      //兼容旧Api
      doms.HeaderOptionRender = headerOptionRender;
    }
    if (showFeedBack) {
      doms.Feedback = (
        <Feedback container={headerContentRef} isI18N={isI18N} app={app} />
      );
    }
    doms.Profile = (
      <Profile
        app={app}
        themeCode={themeCode}
        isI18N={isI18N}
        handleLogout={onUserLogout}
        handleSetting={onUserInfoChange}
        customDropDown={renderModule.profileDropDown}
      />
    );
    if (showFullScreen) {
      doms.FullScreen = <FullScreen />;
    }

    const Layout = React.createElement("div", {
      className: "operation",
    });

    if (!renderModule?.headerOperation?.render) {
      return React.cloneElement(
        Layout,
        {},
        Object.getOwnPropertyNames(doms).map((key) =>
          React.cloneElement(doms[key], { key })
        )
      );
    }
    return renderModule?.headerOperation?.render(
      Object.assign(doms, { Layout })
    );
  }, [
    renderModule?.headerOperation,
    isI18N,
    app,
    themeCode,
    showFeedBack,
    showFullScreen,
    questionnaires,
  ]);

  const [selectCollapsed, setSelectCollapsed] = useState(true);
  const onCollapsed = (value) => {
    setSelectCollapsed(value);
  };

  return (
    <header
      data-app-theme={themeCode || app}
      className="wbuc-appearance-header"
      onClick={stopPropagation}
    >
      {/* 暗色系需要遮罩，满足产品对图片亮度减少要求 */}
      {themeConfig["wbuc.appearance.type"] === "dark" && (
        <div
          className={classNames("wbuc-appearance-header-mask", {
            ["wbuc-appearance-header-mask-collapsed"]: selectCollapsed,
          })}
        ></div>
      )}
      <div className="header-content" ref={headerContentRef}>
        <Logo
          className="logo"
          renderModule={renderModule?.logo}
          collapsed={collapsed}
          app={app}
        />
        <AppSelect
          value={app}
          data={appList}
          isI18N={isI18N}
          collapsed={selectCollapsed}
          onChange={onAppChange}
          renderModule={renderModule?.appSelect}
          appList={renderModule?.appSelect?.appList}
          onCollapsed={onCollapsed}
        />
        {operations}
      </div>
      <div id={`${WE_BANNER_ID}-${app}`} className={"banner"}></div>
    </header>
  );
};

export default Navigation;
